(() => {
  const errorText = (payload, fallback) => {
    const detail = payload?.description || payload?.message || payload?.errors;
    if (typeof detail === 'string') return detail;
    if (detail && typeof detail === 'object') return Object.values(detail).flat().join(' ');
    return fallback;
  };
  const showError = (root, message) => {
    const error = root.querySelector('[data-native-error]');
    if (!error) return;
    error.textContent = message;
    error.hidden = false;
    error.focus();
  };
  const clearError = (root) => {
    const error = root.querySelector('[data-native-error]');
    if (error) { error.textContent = ''; error.hidden = true; }
  };

  class NativeProduct extends HTMLElement {
    connectedCallback() {
      if (this.initialized) { this.connectPageLifecycle(); return; }
      this.form = this.querySelector('form[action*="/cart/add"]');
      this.select = this.querySelector('[data-native-variant]');
      this.quantity = this.querySelector('[data-native-quantity]');
      if (!this.form || !this.select || !this.quantity) return;
      this.initialized = true;
      this.onPageShow = event => this.restoreAfterCartNavigation(event);
      this.variants = Array.from(this.select.options).map(option => ({option, values: JSON.parse(option.dataset.options || '[]')}));
      this.fields = Array.from(this.querySelectorAll('[data-option-position]'));
      this.select.addEventListener('change', () => this.selectVariant(this.select.selectedOptions[0]));
      this.fields.forEach(field => field.addEventListener('change', () => {
        const values = this.fields.map(item => item.querySelector('input:checked')?.value);
        const match = this.variants.find(variant => variant.values.every((value, index) => value === values[index]));
        this.selectVariant(match?.option);
      }));
      this.querySelectorAll('[data-native-step]').forEach(button => button.addEventListener('click', () => {
        if (button.dataset.nativeStep === '-1') this.quantity.stepDown();
        else this.quantity.stepUp();
        this.updateQuantityButtons();
      }));
      this.quantity.addEventListener('input', () => this.updateQuantityButtons());
      this.querySelectorAll('[data-native-media-button]').forEach(button => {
        button.addEventListener('click', () => this.showMedia(button.dataset.nativeMediaButton));
        button.addEventListener('keydown', event => this.navigateMedia(event, button));
      });
      this.form.addEventListener('submit', event => this.addToCart(event));
      this.enhanceDetails();
      this.classList.add('is-enhanced');
      this.selectVariant(this.select.selectedOptions[0], false);
      const sticky = this.querySelector('[data-native-sticky]');
      const buybox = this.querySelector('[data-native-buybox]');
      if (sticky && buybox && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          const entry = entries[0];
          sticky.hidden = entry.isIntersecting || entry.boundingClientRect.bottom > 0;
        });
      }
      this.connectPageLifecycle();
    }
    connectPageLifecycle() {
      window.addEventListener('pageshow', this.onPageShow);
      const buybox = this.querySelector('[data-native-buybox]');
      if (buybox) this.observer?.observe(buybox);
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      window.removeEventListener('pageshow', this.onPageShow);
    }
    restoreAfterCartNavigation(event) {
      if (!event.persisted || !this.cartNavigationPending) return;
      this.cartNavigationPending = false;
      this.busy = false;
      this.form.removeAttribute('aria-busy');
      const variantId = new URL(window.location.href).searchParams.get('variant');
      const option = this.variants.find(variant => variant.option.value === variantId)?.option || this.select.selectedOptions[0];
      this.selectVariant(option, false);
    }
    enhanceDetails() {
      const details = this.querySelector('[data-native-details]');
      const description = this.querySelector('[data-native-description]');
      const navigation = this.querySelector('[data-native-details-nav]');
      if (!details || !description || !navigation) return;
      this.querySelectorAll('[data-native-details-link]').forEach(link => link.addEventListener('click', () => {
        details.open = true;
        details.querySelector('summary')?.focus({preventScroll: true});
      }));
      const headings = Array.from(description.querySelectorAll('h2, h3')).filter(heading => heading.textContent.trim());
      if (headings.length > 1) {
        const list = document.createElement('ul');
        headings.forEach((heading, index) => {
          if (!heading.id) heading.id = `${details.id}-heading-${index + 1}`;
          heading.tabIndex = -1;
          const item = document.createElement('li');
          const link = document.createElement('a');
          link.href = `#${encodeURIComponent(heading.id)}`;
          link.textContent = heading.textContent.trim();
          link.addEventListener('click', () => {
            details.open = true;
            heading.focus({preventScroll: true});
          });
          item.append(link);
          list.append(item);
        });
        navigation.replaceChildren(list);
        navigation.hidden = false;
      }
      let tableLabel = navigation.getAttribute('aria-label');
      description.querySelectorAll('h2, h3, table').forEach(element => {
        if (element.tagName !== 'TABLE') {
          if (element.textContent.trim()) tableLabel = element.textContent.trim();
          return;
        }
        if (element.parentElement?.classList.contains('tns-native-product__table-scroll')) return;
        const wrapper = document.createElement('div');
        wrapper.className = 'tns-native-product__table-scroll';
        wrapper.tabIndex = 0;
        wrapper.setAttribute('role', 'region');
        wrapper.setAttribute('aria-label', element.caption?.textContent.trim() || tableLabel);
        element.before(wrapper);
        wrapper.append(element);
      });
    }
    updateQuantityButtons() {
      const min = Number(this.quantity.min) || 1;
      const max = this.quantity.max ? Number(this.quantity.max) : Infinity;
      const value = Number(this.quantity.value);
      this.querySelectorAll('[data-native-step]').forEach(button => {
        button.disabled = this.busy || (button.dataset.nativeStep === '-1' ? value <= min : value >= max);
      });
    }
    navigateMedia(event, button) {
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      const buttons = Array.from(this.querySelectorAll('[data-native-media-button]'));
      const index = buttons.indexOf(button);
      if (index < 0 || buttons.length < 2) return;
      let next;
      if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = buttons.length - 1;
      else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        const direction = window.getComputedStyle(this).direction === 'rtl' ? -1 : 1;
        next = (index + (event.key === 'ArrowRight' ? direction : -direction) + buttons.length) % buttons.length;
      } else return;
      event.preventDefault();
      buttons[next].focus();
      this.showMedia(buttons[next].dataset.nativeMediaButton);
    }
    showMedia(id) {
      if (!id || !Array.from(this.querySelectorAll('[data-native-media]')).some(media => media.dataset.nativeMedia === id)) return;
      this.querySelectorAll('[data-native-media]').forEach(media => {
        const wasVisible = !media.hidden;
        media.hidden = media.dataset.nativeMedia !== id;
        if (wasVisible && media.hidden) {
          media.querySelectorAll('video').forEach(video => video.pause());
          media.querySelectorAll('iframe').forEach(frame => { frame.src = frame.src; });
        }
      });
      this.querySelectorAll('[data-native-media-button]').forEach(button => button.setAttribute('aria-current', String(button.dataset.nativeMediaButton === id)));
    }
    selectVariant(option, updateUrl = true) {
      clearError(this);
      this.select.disabled = !option;
      this.available = Boolean(option && option.dataset.available === 'true');
      this.querySelectorAll('[data-native-add]').forEach(button => {
        button.disabled = !this.available || this.busy;
        button.textContent = this.available ? this.dataset.addLabel : option ? this.dataset.soldLabel : this.dataset.unavailableLabel;
      });
      if (option) {
        this.select.value = option.value;
        const values = JSON.parse(option.dataset.options || '[]');
        this.fields.forEach((field, index) => {
          field.querySelectorAll('input[type="radio"]').forEach(input => { input.checked = input.value === values[index]; });
        });
        this.querySelectorAll('[data-native-price]').forEach(price => { price.textContent = option.dataset.price; });
        this.querySelectorAll('[data-native-compare]').forEach(price => { price.textContent = option.dataset.compare; price.hidden = !option.dataset.compare; });
        this.quantity.min = option.dataset.min || '1';
        this.quantity.step = option.dataset.step || '1';
        this.quantity.max = option.dataset.max || '';
        const min = Number(this.quantity.min);
        const step = Number(this.quantity.step);
        const max = this.quantity.max ? Number(this.quantity.max) : Infinity;
        const oldValue = Number(this.quantity.value) || min;
        const alignedValue = min + Math.max(0, Math.floor((oldValue - min) / step)) * step;
        this.quantity.value = String(Math.min(alignedValue, max));
        this.showMedia(option.dataset.media);
        if (updateUrl) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', option.value);
          url.searchParams.delete('option_values');
          window.history.replaceState({}, '', url);
        }
      } else {
        this.querySelectorAll('[data-native-price]').forEach(price => { price.textContent = this.dataset.unavailableLabel; });
        this.querySelectorAll('[data-native-compare]').forEach(price => { price.hidden = true; });
      }
      this.fields.forEach(field => {
        const label = field.querySelector('[data-selected-option]');
        if (label) label.textContent = field.querySelector('input:checked')?.value || '';
      });
      this.updateQuantityButtons();
    }
    async addToCart(event) {
      event.preventDefault();
      if (this.busy || !this.available || !this.form.reportValidity()) return;
      clearError(this);
      const body = new FormData(this.form);
      const buttons = Array.from(this.querySelectorAll('[data-native-add]'));
      this.cartNavigationPending = false;
      this.busy = true;
      this.form.setAttribute('aria-busy', 'true');
      buttons.forEach(button => { button.disabled = true; button.textContent = this.dataset.loadingLabel; });
      this.updateQuantityButtons();
      try {
        const response = await fetch(`${this.form.action.replace(/\/$/, '')}.js`, {method: 'POST', credentials: 'same-origin', headers: {Accept: 'application/json'}, body});
        const payload = await response.json();
        if (!response.ok || payload.status >= 400) throw new Error(errorText(payload, this.dataset.errorMessage));
        this.cartNavigationPending = true;
        window.location.assign(this.dataset.cartUrl);
      } catch (error) {
        showError(this, error.message || this.dataset.errorMessage);
        this.cartNavigationPending = false;
        this.busy = false;
        this.form.removeAttribute('aria-busy');
        buttons.forEach(button => { button.disabled = !this.available; button.textContent = this.available ? this.dataset.addLabel : this.dataset.soldLabel; });
        this.updateQuantityButtons();
      }
    }
  }

  class NativeCart extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.form = this.querySelector('[data-native-cart-form]');
      if (!this.form) return;
      this.updateButton = this.querySelector('[data-native-cart-update]');
      this.querySelectorAll('[data-native-cart-line]').forEach(line => {
        const input = line.querySelector('[data-native-cart-quantity]');
        line.querySelectorAll('[data-native-cart-step]').forEach(button => button.addEventListener('click', () => {
          const minimum = Number(input.dataset.minimum) || 1;
          const increment = Number(input.step) || 1;
          const current = Number(input.value) || 0;
          let next = current + Number(button.dataset.nativeCartStep) * increment;
          if (next < minimum) next = button.dataset.nativeCartStep === '-1' ? 0 : minimum;
          if (input.max) next = Math.min(next, Number(input.max));
          input.value = String(Math.max(0, next));
          this.validateQuantity(input);
        }));
        input.addEventListener('input', () => this.validateQuantity(input));
        line.querySelector('[data-native-cart-remove]').addEventListener('click', event => {
          event.preventDefault();
          if (this.busy) return;
          input.value = '0';
          this.validateQuantity(input);
          this.form.requestSubmit(this.updateButton);
        });
      });
      this.form.addEventListener('submit', event => {
        this.querySelectorAll('[data-native-cart-quantity]').forEach(input => this.validateQuantity(input));
        if (this.busy || !this.form.reportValidity()) { event.preventDefault(); return; }
        if (event.submitter?.name === 'checkout') return;
        event.preventDefault();
        this.updateCart();
      });
      this.classList.add('is-enhanced');
    }
    validateQuantity(input) {
      const value = Number(input.value);
      const minimum = Number(input.dataset.minimum) || 1;
      input.setCustomValidity(value > 0 && value < minimum ? this.dataset.quantityError : '');
    }
    async updateCart() {
      if (this.busy) return;
      clearError(this);
      const changes = Array.from(this.querySelectorAll('[data-native-cart-line]')).map(line => ({line: Number(line.dataset.line), quantity: Number(line.querySelector('[data-native-cart-quantity]').value), original: Number(line.dataset.originalQuantity)})).filter(line => line.quantity !== line.original).reverse();
      const note = this.form.querySelector('[name="note"]').value;
      this.busy = true;
      this.form.setAttribute('aria-busy', 'true');
      const controls = Array.from(this.form.querySelectorAll('button, input, textarea'));
      controls.forEach(control => { control.disabled = true; });
      try {
        const send = async (url, data) => {
          const response = await fetch(url, {method: 'POST', credentials: 'same-origin', headers: {Accept: 'application/json', 'Content-Type': 'application/json'}, body: JSON.stringify(data)});
          const payload = await response.json();
          if (!response.ok || payload.status >= 400) throw new Error(errorText(payload, this.dataset.errorMessage));
          return payload;
        };
        await send(this.dataset.updateUrl, {note});
        for (const change of changes) await send(this.dataset.changeUrl, {line: change.line, quantity: change.quantity});
        window.location.reload();
      } catch (error) {
        const message = error.message || this.dataset.errorMessage;
        showError(this, message);
        this.form.removeAttribute('aria-busy');
        this.querySelector('[data-native-cart-reload]').hidden = false;
        try {
          const url = new URL(this.form.action);
          url.searchParams.set('section_id', this.dataset.sectionId);
          const response = await fetch(url, {credentials: 'same-origin', headers: {Accept: 'text/html'}, cache: 'no-store'});
          if (!response.ok) return;
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const fresh = doc.querySelector('tns-native-cart');
          if (!fresh) return;
          this.replaceWith(fresh);
          showError(fresh, message);
        } catch (_) {
          this.busy = true;
        }
      }
    }
  }

  class NativeRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.initialized || this.querySelector('[data-native-recommendation-content]') || !this.dataset.url) return;
      this.initialized = true;
      fetch(this.dataset.url, {credentials: 'same-origin', headers: {Accept: 'text/html'}})
        .then(response => {
          if (!response.ok) throw new Error('Recommendations unavailable');
          return response.text();
        })
        .then(html => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const content = doc.querySelector('[data-native-recommendation-content]');
          if (content?.querySelector('.tns-native-card')) { this.replaceChildren(content); this.hidden = false; }
        })
        .catch(() => { this.hidden = true; });
    }
  }
  if (!customElements.get('tns-native-product')) customElements.define('tns-native-product', NativeProduct);
  if (!customElements.get('tns-native-cart')) customElements.define('tns-native-cart', NativeCart);
  if (!customElements.get('tns-native-recommendations')) customElements.define('tns-native-recommendations', NativeRecommendations);
})();
